import { z } from "zod";
/** Rings for manual release operations (C5). Changing ring never deploys. */
export const FLEET_RINGS = ["pilot", "canary", "production"];
export const DEPLOYMENT_PHASES = [
    "intended",
    "installed_recorded",
    "verified",
    "superseded",
    "cancelled",
];
export const VERIFICATION_STATUSES = [
    "unverified",
    "verified_match",
    "verified_mismatch",
    "skipped",
];
export const ROLLBACK_DRILL_STATUSES = ["not_started", "drilled", "waived"];
/** D4: unverified intent older than this appears as unverified drift (display-only). */
export const UNVERIFIED_INTENT_DRIFT_MS = 7 * 24 * 60 * 60 * 1000;
const plain = (max) => z.string().trim().min(1).max(max);
const optionalPlain = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === undefined || v === "" ? null : v));
/**
 * Reject accidental secret material in free-text fleet fields.
 * Inventory must never hold credentials, keys, tokens, config exports, or topology dumps.
 */
export function looksLikeSecretMaterial(value) {
    const v = value.trim();
    if (!v)
        return false;
    if (/BEGIN (RSA |OPENSSH |EC )?PRIVATE KEY/i.test(v))
        return true;
    if (/BEGIN CERTIFICATE/i.test(v))
        return true;
    if (/-----BEGIN/i.test(v) && /-----END/i.test(v))
        return true;
    if (/\b(api[_-]?token|access[_-]?token|refresh[_-]?token)\s*[:=]/i.test(v))
        return true;
    if (/\b(password|passwd|secret|private[_-]?key)\s*[:=]\s*\S+/i.test(v))
        return true;
    if (/\b(ssh-rsa|ssh-ed25519)\s+[A-Za-z0-9+/=]{40,}/i.test(v))
        return true;
    // Heuristic: very large dumps resemble config/topology exports
    if (v.length > 4000)
        return true;
    return false;
}
function rejectSecrets(field) {
    return (val) => {
        if (val == null || val === "")
            return true;
        return !looksLikeSecretMaterial(val);
    };
}
export const upsertRouterInventorySchema = z.object({
    serialAssetTag: optionalPlain(120),
    managementHost: optionalPlain(255),
    routerModel: optionalPlain(120),
    routerosVersion: optionalPlain(80),
    hotspotProfileName: optionalPlain(120),
    portalDirectoryPath: optionalPlain(255),
    ring: z.enum(FLEET_RINGS).optional(),
    notes: optionalPlain(2000),
    /** Required when promoting ring to production (D5). Ignored otherwise. */
    password: z.string().min(1).max(128).optional(),
}).superRefine((data, ctx) => {
    for (const [key, value] of Object.entries(data)) {
        if (key === "password" || key === "ring")
            continue;
        if (typeof value === "string" && looksLikeSecretMaterial(value)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Field ${key} must not contain credentials, keys, tokens, or config exports`,
                path: [key],
            });
        }
    }
});
export const createDeploymentIntentSchema = z.object({
    siteId: z.string().min(1),
    bundleArtifactId: z.string().min(1),
    approvedRollbackArtifactId: z.string().min(1).nullable().optional(),
    evidenceNotes: optionalPlain(2000),
}).superRefine((data, ctx) => {
    if (data.evidenceNotes && looksLikeSecretMaterial(data.evidenceNotes)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Evidence notes must not contain credentials, keys, tokens, or config exports",
            path: ["evidenceNotes"],
        });
    }
});
export const recordInstallSchema = z.object({
    installedReleaseId: plain(80),
    installedSha256: optionalPlain(64),
    evidenceNotes: optionalPlain(2000),
}).superRefine((data, ctx) => {
    if (data.evidenceNotes && looksLikeSecretMaterial(data.evidenceNotes)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Evidence notes must not contain credentials, keys, tokens, or config exports",
            path: ["evidenceNotes"],
        });
    }
    if (data.installedSha256 && !/^[a-f0-9]{64}$/i.test(data.installedSha256)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "installedSha256 must be a 64-char hex digest when provided",
            path: ["installedSha256"],
        });
    }
});
export const verifyDeploymentSchema = z.object({
    verificationStatus: z.enum(["verified_match", "verified_mismatch", "skipped"]),
    driftReason: optionalPlain(1000),
    evidenceNotes: optionalPlain(2000),
    password: z.string().min(1).max(128).optional(),
}).superRefine((data, ctx) => {
    if (data.verificationStatus === "verified_mismatch" && !data.driftReason) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "driftReason required for verified_mismatch",
            path: ["driftReason"],
        });
    }
    if (data.verificationStatus === "verified_mismatch" && !data.password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "password required for verified_mismatch",
            path: ["password"],
        });
    }
    for (const key of ["driftReason", "evidenceNotes"]) {
        const val = data[key];
        if (val && looksLikeSecretMaterial(val)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${key} must not contain credentials, keys, tokens, or config exports`,
                path: [key],
            });
        }
    }
});
export const updateRollbackDrillSchema = z.object({
    rollbackDrillStatus: z.enum(ROLLBACK_DRILL_STATUSES),
    password: z.string().min(1).max(128).optional(),
}).superRefine((data, ctx) => {
    if (data.rollbackDrillStatus === "waived" && !data.password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "password required to waive rollback drill",
            path: ["password"],
        });
    }
});
/** Forbidden request keys — never accepted as inventory/deployment storage fields. */
export const FORBIDDEN_FLEET_STORAGE_KEYS = [
    "passwd",
    "secret",
    "apiToken",
    "api_token",
    "accessToken",
    "privateKey",
    "private_key",
    "sshKey",
    "ssh_key",
    "credential",
    "credentials",
    "configExport",
    "config_export",
    "firewallRules",
    "firewall_rules",
    "topology",
    "fullConfig",
    "full_config",
];
export function assertNoForbiddenFleetKeys(body) {
    if (!body || typeof body !== "object" || Array.isArray(body))
        return { ok: true };
    for (const key of Object.keys(body)) {
        if (FORBIDDEN_FLEET_STORAGE_KEYS.includes(key)) {
            return {
                ok: false,
                error: `Field '${key}' is forbidden — fleet inventory stores only non-secret operational metadata`,
            };
        }
    }
    return { ok: true };
}
//# sourceMappingURL=fleet.js.map